/*
 * ॐ नमः शिवाय
 *
 * Copyright (c) 2025 Rudra Coresoft Technologies Private Limited
 *
 * This software is licensed under the VIJAYA 1.0 License.
 *
 * You are free to use, modify, and distribute this software under the terms 
 * of the VIJAYA 1.0 License.
 *
 * MANDATORY ATTRIBUTION:
 * 1. The attribution to Rudra Coresoft Technologies Private Limited in this 
 *    copyright notice must not be removed even after modifications.
 * 2. For Free License usage, the footer phrases "Powered by arcee.io" and 
 *    "Developed by Rudra Coresoft" (plus logos) must remain visible.
 *
 * See the LICENSE file for the full text.
 */

/**
 * ApplicationSettingsForm Component
 * ------------------------
 * Allows users to configure security and authentication settings 
 *
 * Features:
 * Displays a set of predefined boolean security settings.
 * Allows toggling features like MFA, social login, and row-level security.
 * Handles API calls to store application settings and generate OAuth2 client credentials.
 * Uses dialogs for displaying success/error messages and credentials.
 * Integrates with Themed components for consistent UI styling.
 *
 * Props:
 * initialState: Object with prefilled values for form fields.
 * onSubmit: Optional callback after successful settings save.
 */

import React, { useState } from "react";
import { useTheme } from "../theme/ThemeContext";
import ThemedButton from "./common/ThemedButton";
import APIHelper from '../api/APIHelper';
import envConfig from "../config/envConfig";
import { useNavigate } from 'react-router-dom';
import { useParams } from 'react-router-dom';
import { useAlertDialog } from "./common/AlertDialogContext";
import { handleErrorResponse } from "../utils/utils";
import ClientKeySecretDialog from "./common/ClientKeySecretDialog";



const apiHelper = new APIHelper({
    apiUrl: envConfig.backendApiUrl,
    tokenUrl: envConfig.backendTokenUrl,
    clientId: envConfig.clientKey,
    clientSecret: envConfig.clientSecret,
});

const BOOLEAN_FIELDS = [
    {
        key: "email_password_login",
        label: "Email/Password Login",
        help: "Standard email/password login enabled",
        locked: true // field is always enabled, not user-toggleable
    },
    {
        key: "user_registrations_allowed",
        label: "User Registrations Allowed",
        help: "Are user registrations allowed",
    },
    {
        key: "social_login_enabled",
        label: "Social Login via OAuth",
        help: "Social login via OAuth providers",
    },
    {
        key: "phone_login_enabled",
        label: "Phone Login",
        help: "Phone number login enabled",
    },
    {
        key: "passwordless_login",
        label: "Passwordless Login",
        help: "Passwordless login with magic links",
    },
    {
        key: "mfa_enabled",
        label: "Multi-factor Authentication",
        help: "Multi-factor authentication enabled",
    },
    {
        key: "sso_saml_enabled",
        label: "SSO (SAML) Enabled",
        help: "Single Sign-On (SAML) with SSO enabled",
    },
    {
        key: "captcha_protection",
        label: "CAPTCHA Protection",
        help: "CAPTCHA protection enabled",
    },
    {
        key: "row_level_security",
        label: "Row Level Security",
        help: "Row Level Security Enforcement",
    },
    {
        key: "audit_trails",
        label: "Audit Trails",
        help: "Audit trails enabled",
    },
];

/**
 * ApplicationSettingsForm component for managing application security and login settings.
 * @param {object} initialState - Initial values to seed the form controls.
 * @param {function} onSubmit - Optional callback invoked after successful form submission.
 */
export default function ApplicationSettingsForm({ initialState = {}, onSubmit }) {
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState(null);
    const { id } = useParams();
    const { showAlertDialog } = useAlertDialog();

    const [cksOpen, setClientKeySecretOpen] = useState(false);
    const [clientKey, setClientKey] = useState("");
    const [clientSecret, setClientSecret] = useState("");


    const [form, setForm] = useState({
        email_password_login: true,          // Always enabled
        user_registrations_allowed: false,
        social_login_enabled: false,
        phone_login_enabled: false,
        passwordless_login: false,
        mfa_enabled: false,
        sso_saml_enabled: false,
        captcha_protection: false,
        row_level_security: false,
        audit_trails: false,
        ...initialState,
    });

    function handleToggle(field) {
        setForm(f => ({
            ...f,
            [field]: !f[field],
        }));
    }

    /**
     * Generates the Application Client ID and Secret via API call to the backend.
     */
    async function generateApplicationCredentials() {
        // Your logic to get new keys...
        //API call
        try {
            const resp = await apiHelper.apiCall('/arcee/application/oauth-application/create', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ application_id: id }),
            });

            if (!resp.ok) {
                await handleErrorResponse(resp);
                return;
            }

            const data = await resp.json();

            setClientKey(data.client_id || "");
            setClientSecret(data.client_secret || "");
            setClientKeySecretOpen(true);
        } catch (error) {
            console.error("Failed to generate credentials:", error);
            showAlertDialog({
                title: "Failed to generate credentials",
                message: error.message,
                type: "error",
                okLabel: "Retry",
                showCancel: false,
                onOk: () => {
                    generateApplicationCredentials()
                }
            });
        }
    }

    /**
     * Handles closing the ClientKeySecretDialog and navigates back to applications list.
     */
    async function handleClose() {
        setClientKeySecretOpen(false);
        navigate('/dashboard/applications');
    }

    /**
     * Handles the submit
     */
    async function handleSubmit(e) {
        e.preventDefault();
        setLoading(true);
        setError(null);

        console.log(id)
        const payload = {
            application: id,
            ...form,
        };

        try {
            const resp = await apiHelper.apiCall('/arcee/application/settings/create', {
                method: 'POST',
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!resp.ok) {
                await handleErrorResponse(resp);
            }

            const data = await resp.json();

            showAlertDialog({
                title: "Success",
                message: "Application settings stored successfully. Please generate the application credentials to finish the setup.",
                type: "success",
                okLabel: "Continue",
                showCancel: false,
                onOk: () => {
                    generateApplicationCredentials();

                }
            });

            if (onSubmit) {
                onSubmit(data);
            }

        } catch (error) {
            console.log("Error in creating application settings: ", error);
            showAlertDialog({
                title: "Could not create application settings, Please try again.",
                message: error.message,
                type: "error",
                okLabel: "OK",
                showCancel: false,
                onOk: () => {
                    navigate(0)
                }
            });
        } finally {
            setLoading(false);
        }
    }
    function handleSkip(e) {
        e.preventDefault();
        showAlertDialog({
            title: "Skip",
            message: "Create application Settings skipped",
            type: "info",
            okLabel: "OK",
            showCancel: false,
            onOk: () => {
                navigate('/dashboard/applications');
            }
        });
    }

    return (
        <div style={{
            maxWidth: 520,
            margin: "50px auto",
            padding: "0 10px",
            fontFamily: theme.fontFamily,
        }}>

            <ClientKeySecretDialog
                open={cksOpen}
                onClose={handleClose}
                clientKey={clientKey}
                clientSecret={clientSecret}
                canRegenerate={true}
                onRegenerate={generateApplicationCredentials}
            />

            {/* Gradient Bar */}
            <div
                style={{
                    width: "100%",
                    height: 10,
                    borderTopLeftRadius: theme.cardRadius,
                    borderTopRightRadius: theme.cardRadius,
                    background:
                        theme.gradientBackground ||
                        `linear-gradient(90deg, ${theme.primary}, ${theme.secondary})`,
                    marginBottom: -3,
                }}
            />
            <form
                style={{
                    background: "#fff",
                    borderRadius: theme.cardRadius,
                    boxShadow: "0 4px 24px rgba(65,70,150,0.13)",
                    padding: "28px 24px 16px 24px",
                    color: theme.text,
                }}
                onSubmit={handleSubmit}
                noValidate
                autoComplete="off"
            >
                <h2 style={{
                    color: theme.primary,
                    fontFamily: theme.fontFamily,
                    fontWeight: 700,
                    marginBottom: 20,
                    fontSize: "1.6rem"
                }}>
                    Security & Login Settings
                </h2>

                {BOOLEAN_FIELDS.map(field => {
                    const isLocked = !!field.locked;
                    return (
                        <div
                            key={field.key}
                            style={{
                                display: "flex",
                                alignItems: "flex-start",
                                marginBottom: 18,
                                gap: 14,
                                opacity: isLocked ? 0.62 : 1 // visually grey out if non-editable
                            }}
                        >
                            <label style={{
                                display: "flex",
                                alignItems: "center",
                                cursor: isLocked ? "not-allowed" : "pointer",
                            }}>
                                <input
                                    type="checkbox"
                                    checked={!!form[field.key]}
                                    onChange={isLocked ? undefined : () => handleToggle(field.key)}
                                    disabled={isLocked}
                                    style={{
                                        accentColor: theme.primary,
                                        width: 20,
                                        height: 20,
                                        marginRight: 10,
                                        borderRadius: theme.buttonRadius,
                                        cursor: isLocked ? "not-allowed" : "pointer",
                                    }}
                                />
                                <span style={{
                                    fontWeight: 600,
                                    fontSize: 15,
                                    color: theme.primary
                                }}>{field.label}</span>
                                {isLocked && (
                                    <i
                                        className="bi bi-lock-fill"
                                        style={{
                                            color: theme.primary,
                                            fontSize: 19,
                                            marginLeft: 8,
                                            opacity: 0.55
                                        }}
                                        title="Always enabled"
                                    />
                                )}
                            </label>
                            <span
                                style={{
                                    fontSize: 13,
                                    color: "#64748b",
                                    marginTop: 2,
                                    flex: 1
                                }}
                            >{field.help}</span>
                        </div>
                    );
                })}

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 26 }}>
                    <ThemedButton type="submit">
                        Save Settings
                    </ThemedButton>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
                    <ThemedButton variant="outlined" onClick={handleSkip}>
                        Skip
                    </ThemedButton>
                </div>
            </form>
        </div>
    );
}
